import React, { useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  Home,
  BookOpen,
  Guitar,
  Music,
  Timer,
  Mic,
  ShoppingBag,
  Shield,
  Search,
  Sun,
  Moon
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore.js';
import { useThemeStore } from '../../stores/themeStore.js';
import { useSyncStore } from '../../stores/syncStore.js';
import { SyncStatus, SyncStatusState } from '../ui/SyncStatus.js';
import { useToast } from '../ui/useToast.js';
import { Avatar } from '../ui/Avatar.js';
import { apiClient } from '../../utils/apiClient.js';
import { toPersianDigits } from '../../utils/timeUtils.js';
import { useUnseenEdits } from '../../hooks/useUnseenEdits.js';

// FND-07: visible ring (2px, primary on base-200 is above 3:1 in both themes) on every sidebar focusable.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-base-200';

export interface SidebarProps {
  syncState?: SyncStatusState;
  queuedSyncCount?: number;
  onSyncClick?: () => void;
  onSearchClick?: () => void;
  onAccountClick?: () => void;
  pendingModerationCount?: number;
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  syncState,
  queuedSyncCount,
  onSyncClick,
  onSearchClick,
  onAccountClick,
  pendingModerationCount,
  className = ''
}) => {
  const { user, isGuest } = useAuthStore();
  const hasUnseen = useUnseenEdits();
  const { theme, toggleTheme } = useThemeStore();
  const toast = useToast();
  const {
    status: storeStatus,
    queuedSessionsCount,
    dirtyRoutinesCount,
    triggerSync
  } = useSyncStore();

  const totalQueued = queuedSessionsCount + dirtyRoutinesCount;
  const effectiveState = syncState !== undefined && onSyncClick !== undefined ? syncState : storeStatus;
  const effectiveQueued = queuedSyncCount !== undefined && onSyncClick !== undefined ? queuedSyncCount : totalQueued;

  const handleManualSync = async () => {
    if (onSyncClick) {
      onSyncClick();
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      toast.warning('دستگاه در حالت آفلاین است.');
      return;
    }
    const res = await triggerSync();
    if (res.success) {
      toast.success('تغییرات با موفقیت همگام شد.');
    } else {
      toast.error(res.message || 'خطا در برقراری ارتباط با سرور');
    }
  };
  const isAdmin = user?.role === 'ADMIN';

  const [fetchedCount, setFetchedCount] = useState<number>(0);

  useEffect(() => {
    if (!isAdmin) return;
    let isMounted = true;
    apiClient
      .get<{ PENDING: number; edits?: { PENDING: number } }>('/api/admin/moderation/counts')
      .then((res) => {
        if (isMounted && typeof res?.PENDING === 'number') {
          const editsPending = res.edits?.PENDING ?? 0;
          setFetchedCount(res.PENDING + editsPending);
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  const adminCount = pendingModerationCount ?? fetchedCount;

  const navItems = [
    { to: '/dashboard', label: 'خانه', icon: Home, testId: 'nav-link-dashboard' },
    { to: '/tabs', label: 'کتابخانه', icon: BookOpen, testId: 'nav-link-library' },
    { to: '/chords', label: 'آکوردها', icon: Guitar, testId: 'nav-link-chords' },
    { to: '/scales', label: 'گام‌ها', icon: Music, testId: 'nav-link-scales' },
    { to: '/routines', label: 'روتین‌ها', icon: Timer, testId: 'nav-link-routines' },
    { to: '/tuner', label: 'تیونر', icon: Mic, testId: 'nav-link-tuner' },
    { to: '/marketplace', label: 'بازارچه', icon: ShoppingBag, testId: 'nav-link-marketplace' }
  ];

  const displayName = isGuest ? 'کاربر مهمان' : user?.fullName || 'کاربر';
  const subtitle = isGuest ? 'حالت آفلاین' : 'حساب و تنظیمات';

  return (
    <aside
      data-testid="desktop-sidebar"
      className={`hidden lg:flex flex-col w-64 h-full sticky top-0 bg-base-200 border-e border-base-300 px-4 py-5 gap-6 select-none shrink-0 z-30 ${className}`}
    >
      <div className="flex flex-col min-h-0 w-full flex-1 gap-6">
        {/* Brand Header */}
        <Link
          to="/dashboard"
          className={`flex items-center gap-2.5 px-1 rounded-xl text-base-content hover:opacity-90 transition ${FOCUS_RING}`}
        >
          <img
            src="/icon.svg"
            alt="FretBoard"
            className="size-9 rounded-[10px] object-cover shrink-0"
          />
          <div className="flex flex-col">
            <span className="text-base font-extrabold tracking-tight" dir="ltr">
              FretBoard
            </span>
            <span className="text-[11px] text-base-content/60 font-medium">فرت‌بورد</span>
          </div>
        </Link>

        {/* Global Search Trigger */}
        <button
          type="button"
          data-testid="global-search-trigger"
          onClick={onSearchClick}
          className={`h-10 flex items-center gap-2 px-3 rounded-xl bg-base-100 border border-base-300 text-base-content/60 hover:text-base-content hover:border-base-content/30 transition text-[13px] text-right cursor-pointer ${FOCUS_RING}`}
        >
          <Search className="size-4 shrink-0" />
          <span className="flex-1">جستجو</span>
          <kbd
            dir="ltr"
            className="font-mono text-[11px] border border-base-300 rounded-md px-1.5 py-0.5 text-base-content/60 bg-base-200"
          >
            ⌘K
          </kbd>
        </button>

        {/* Primary Navigation */}
        <nav aria-label="ناوبری اصلی" className="flex flex-col gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `h-10 flex items-center gap-3 px-3 rounded-xl text-sm font-semibold transition active:scale-[0.98] ${FOCUS_RING} ${
                    isActive
                      ? 'bg-primary/[0.14] text-primary-soft'
                      : 'bg-transparent text-base-content/80 hover:bg-base-300/60 hover:text-base-content'
                  }`
                }
              >
                <Icon className="size-[18px] shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}

          {/* Admin Moderation Section */}
          {isAdmin && (
            <>
              <div className="h-px bg-base-300 my-2" />
              <NavLink
                to="/admin/moderation"
                data-testid="nav-link-admin"
                className={({ isActive }) =>
                  `h-10 flex items-center justify-between px-3 rounded-xl text-sm font-semibold transition active:scale-[0.98] ${FOCUS_RING} ${
                    isActive
                      ? 'bg-primary/[0.14] text-primary-soft'
                      : 'bg-transparent text-base-content/80 hover:bg-base-300/60 hover:text-base-content'
                  }`
                }
              >
                <span className="flex items-center gap-3 flex-1">
                  <Shield className="size-[18px] shrink-0" />
                  <span>بررسی تبلچرها</span>
                </span>
                {adminCount > 0 && (
                  <span
                    data-testid="nav-admin-badge"
                    className="min-w-5 h-5 px-1.5 rounded-full bg-warning text-base-100 text-[11px] font-bold inline-flex items-center justify-center"
                  >
                    {toPersianDigits(adminCount)}
                  </span>
                )}
              </NavLink>
            </>
          )}
        </nav>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Bottom Cluster: SyncStatus + Account Row */}
        <div className="flex flex-col gap-2 pt-2">
          <SyncStatus
            state={effectiveState}
            queuedCount={effectiveQueued}
            onClick={handleManualSync}
            data-testid="sync-status-pill"
            className="w-full justify-start"
          />

          <div className="flex items-center gap-2.5 px-1 py-1 rounded-xl">
            <button
              type="button"
              data-testid="account-menu-trigger"
              onClick={onAccountClick}
              className={`flex items-center gap-2.5 flex-1 min-w-0 text-start cursor-pointer hover:opacity-85 transition group rounded-lg p-0.5 ${FOCUS_RING}`}
            >
              <div className="relative">
                <Avatar name={displayName} size="sm" />
                {hasUnseen && (
                  <span
                    data-testid="account-unseen-dot"
                    className="absolute top-0 end-0 size-2 rounded-full bg-primary ring-2 ring-base-200"
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-[13px] font-semibold text-base-content truncate group-hover:text-primary transition-colors">
                  {displayName}
                </span>
                <span className="text-[11px] text-base-content/60 truncate">
                  {subtitle}
                </span>
              </div>
            </button>

            <button
              type="button"
              data-testid="theme-toggle-button"
              onClick={toggleTheme}
              aria-label="تغییر تم"
              className={`size-9 rounded-xl inline-flex items-center justify-center text-base-content/60 hover:text-base-content hover:bg-base-300/60 transition cursor-pointer ${FOCUS_RING}`}
            >
              {theme === 'dark' ? (
                <Sun className="size-[18px]" />
              ) : (
                <Moon className="size-[18px]" />
              )}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

Sidebar.displayName = 'Sidebar';
